/**
 * 3D Secure Modal for in-page authentication
 *
 * Opens an overlay with iframe containing the 3DS challenge page.
 * Listens for completion via postMessage and auto-closes.
 */

export class ThreeDSModal {
  private overlay: HTMLElement | null = null;
  private iframe: HTMLIFrameElement | null = null;
  private messageListener: ((event: MessageEvent) => void) | null = null;
  private resolveCompletion: ((success: boolean) => void) | null = null;

  /**
   * Open the 3DS modal with challenge URL
   * Returns a promise that resolves when 3DS is complete
   */
  async open(challengeUrl: string): Promise<boolean> {

    // SECURITY: Validate and sanitize URL
    if (!this.isValidChallengeUrl(challengeUrl)) {
      throw new Error('Invalid 3DS challenge URL');
    }

    // Prevent multiple instances
    if (this.overlay) {
      this.close();
    }

    // Create overlay using safe DOM manipulation (not innerHTML with user data)
    this.overlay = document.createElement('div');
    this.overlay.className = 'inflowpay-3ds-overlay';

    const modal = document.createElement('div');
    modal.className = 'inflowpay-3ds-modal';

    const header = document.createElement('div');
    header.className = 'inflowpay-3ds-header';
    header.innerHTML = '<h3>Secure Payment Authentication</h3><button class="inflowpay-3ds-close" aria-label="Close">×</button>';

    const content = document.createElement('div');
    content.className = 'inflowpay-3ds-content';
    content.innerHTML = '<div class="inflowpay-3ds-loading"><div class="inflowpay-spinner"></div><p>Loading authentication...</p></div>';

    // Create iframe safely
    this.iframe = document.createElement('iframe');
    this.iframe.className = 'inflowpay-3ds-iframe';
    this.iframe.src = challengeUrl; // Safe - URL already validated
    this.iframe.allow = 'payment';
    // Note: allow-same-origin is needed for 3DS providers to make API calls
    // This is safe because:
    // 1. We validate the challengeUrl domain (whitelist)
    // 2. 3DS providers are trusted payment processors
    // 3. Without it, 3DS authentication fails with CORS errors
    this.iframe.sandbox.add('allow-forms', 'allow-scripts', 'allow-same-origin', 'allow-popups');

    content.appendChild(this.iframe);
    modal.appendChild(header);
    modal.appendChild(content);
    this.overlay.appendChild(modal);

    // Add styles
    this.injectStyles();

    // Add to DOM
    document.body.appendChild(this.overlay);

    // Get references
    const closeBtn = this.overlay.querySelector('.inflowpay-3ds-close');
    const loadingEl = this.overlay.querySelector('.inflowpay-3ds-loading') as HTMLElement;

    // Hide loading when iframe loads
    this.iframe.addEventListener('load', () => {
      if (loadingEl) loadingEl.style.display = 'none';
      if (this.iframe) this.iframe.style.display = 'block';
    });

    // Close button handler - FIXED: resolve BEFORE close
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        if (this.resolveCompletion) this.resolveCompletion(false);
        this.close();
      });
    }

    // Setup postMessage listener
    return new Promise<boolean>((resolve) => {
      this.resolveCompletion = resolve;
      this.setupPostMessageListener();
    });
  }

  /**
   * Close and cleanup the modal
   */
  close(): void {

    // Remove message listener
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
      this.messageListener = null;
    }

    // Remove overlay from DOM
    if (this.overlay && this.overlay.parentNode) {
      this.overlay.parentNode.removeChild(this.overlay);
    }

    this.overlay = null;
    this.iframe = null;
    this.resolveCompletion = null;
  }

  /**
   * Listen for postMessage from callback page
   */
  private setupPostMessageListener(): void {
    this.messageListener = (event: MessageEvent) => {
      // SECURITY: Validate origin - only accept messages from same origin or trusted domains
      const currentOrigin = window.location.origin;

      // Allow messages from:
      // 1. Same origin (our callback page in iframe with allow-same-origin)
      // 2. Basis Theory 3DS domains
      // 3. InflowPay API domains (for hosted callback pages)
      const trustedOrigins = [
        currentOrigin,
        'https://api.basistheory.com',
        'https://acs.basistheory.com',
        'https://api.inflowpay.xyz',
        'https://pre-prod.api.inflowpay.xyz',
        'http://localhost:3000',
      ];

      const isTrusted = trustedOrigins.some(origin =>
        event.origin === origin ||
        event.origin.endsWith('.basistheory.com') ||
        event.origin.endsWith('.inflowpay.xyz')
      );

      if (!isTrusted) {
        return;
      }

      // Parse message data
      const data = event.data;

      // Check for 3DS completion message
      if (data && (data.type === 'THREE_DS_COMPLETE' || data.type === '3DS_COMPLETE')) {
        const success = data.status === 'success';

        // Resolve promise BEFORE closing (close() sets resolveCompletion to null)
        if (this.resolveCompletion) {
          this.resolveCompletion(success);
        }

        // Close modal after resolving
        this.close();
      }

      // Also check for redirect to callback URL pattern
      // This handles cases where iframe redirects to our callback URL
      if (this.iframe && this.iframe.contentWindow) {
        try {
          const iframeUrl = this.iframe.contentWindow.location.href;

          // Check if redirected to success/failure callback (both old and new patterns)
          // Old pattern: /3ds-callback.html?status=success
          // New pattern: /threeds/success or /threeds/failure
          if (iframeUrl.includes('3ds-callback') || iframeUrl.includes('/threeds/')) {
            const isSuccess = iframeUrl.includes('status=success') ||
                            iframeUrl.includes('/threeds/success');

            // Resolve promise BEFORE closing
            if (this.resolveCompletion) {
              this.resolveCompletion(isSuccess);
            }
            this.close();
          }
        } catch (e) {
          // Cross-origin error - expected when iframe is on different domain
          // This is fine, we'll rely on postMessage instead
        }
      }
    };

    window.addEventListener('message', this.messageListener);
  }

  /**
   * Inject modal styles into the page
   */
  private injectStyles(): void {
    const styleId = 'inflowpay-3ds-modal-styles';

    // Don't inject if already present
    if (document.getElementById(styleId)) {
      return;
    }

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .inflowpay-3ds-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 999999;
        animation: inflowpay-fadeIn 0.2s ease-out;
      }

      @keyframes inflowpay-fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }

      .inflowpay-3ds-modal {
        background: white;
        border-radius: 12px;
        width: 90%;
        max-width: 600px;
        max-height: 90vh;
        display: flex;
        flex-direction: column;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        animation: inflowpay-slideUp 0.3s ease-out;
      }

      @keyframes inflowpay-slideUp {
        from { transform: translateY(20px); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
      }

      .inflowpay-3ds-header {
        padding: 20px 24px;
        border-bottom: 1px solid #e5e7eb;
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      .inflowpay-3ds-header h3 {
        margin: 0;
        font-size: 18px;
        font-weight: 600;
        color: #1f2937;
      }

      .inflowpay-3ds-close {
        background: none;
        border: none;
        font-size: 32px;
        line-height: 1;
        color: #6b7280;
        cursor: pointer;
        padding: 0;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 6px;
        transition: all 0.2s;
      }

      .inflowpay-3ds-close:hover {
        background: #f3f4f6;
        color: #1f2937;
      }

      .inflowpay-3ds-content {
        position: relative;
        flex: 1;
        overflow: hidden;
        min-height: 500px;
        display: flex;
        flex-direction: column;
      }

      .inflowpay-3ds-loading {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        background: white;
        z-index: 1;
      }

      .inflowpay-3ds-loading p {
        margin-top: 16px;
        color: #6b7280;
        font-size: 14px;
      }

      .inflowpay-spinner {
        width: 40px;
        height: 40px;
        border: 3px solid #e5e7eb;
        border-top-color: #3b82f6;
        border-radius: 50%;
        animation: inflowpay-spin 0.8s linear infinite;
      }

      @keyframes inflowpay-spin {
        to { transform: rotate(360deg); }
      }

      .inflowpay-3ds-iframe {
        display: none;
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        width: 100%;
        height: 100%;
        border: none;
      }

      /* Mobile responsive */
      @media (max-width: 640px) {
        .inflowpay-3ds-modal {
          width: 100%;
          max-width: 100%;
          height: 100%;
          max-height: 100%;
          border-radius: 0;
        }

        .inflowpay-3ds-content {
          min-height: 0;
        }
      }
    `;

    document.head.appendChild(style);
  }

  /**
   * Validate challenge URL to prevent XSS
   */
  private isValidChallengeUrl(url: string): boolean {
    try {
      const parsedUrl = new URL(url);

      // Only allow HTTPS (except localhost for development)
      const isSecure = parsedUrl.protocol === 'https:' ||
                      (parsedUrl.protocol === 'http:' && parsedUrl.hostname === 'localhost');

      if (!isSecure) {
        return false;
      }

      // Whitelist trusted domains for 3DS
      const trustedDomains = [
        'basistheory.com',
        'stripe.com',
        'localhost', // For development
      ];

      const isTrustedDomain = trustedDomains.some(domain =>
        parsedUrl.hostname === domain || parsedUrl.hostname.endsWith('.' + domain)
      );

      if (!isTrustedDomain) {
        return false;
      }

      return true;
    } catch (e) {
      return false;
    }
  }
}
